import React, { useState, useRef, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Row, Col, Card, CardBody, Table, Alert, Badge } from 'reactstrap';
import { createDataRequest, getTeeListRequest } from '../../api/controller/TeeIssueController';
import { useSetRecoilState } from 'recoil';
import { teeIssueTeeState } from '../../app/auth/teeIssues/state';
import notify from '../helpers/Notify';
import { Loader } from './Loader';
import { handleResponse } from '../helpers/HandleResponse';
import echo from '../../api/core/echo';
import { useCallback } from 'react';

export const POSModal = ({ isOpen, toggle }) => {
    const [barcodeInput, setBarcodeInput] = useState('');
    const [scannedTee, setScannedTee] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [buyerName, setBuyerName] = useState('');
    const [buyerContact, setBuyerContact] = useState('');
    const [cart, setCart] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [teesData, setTeesData] = useState([]);
    const [teesLoading, setTeesLoading] = useState(false);
    const barcodeInputRef = useRef(null);
    const [showShortcuts, setShowShortcuts] = useState(false);
    
    // Get tees data from frontend state for initial load
    const setTeeIssueTeeData = useSetRecoilState(teeIssueTeeState);

    // Fetch full Tee dataset for POSModal
    const fetchTeesData = async () => {
        setTeesLoading(true);
        try {
            const response = await getTeeListRequest();
            if (response && response.Status !== 'error') {
                setTeesData(response.data || []);
                // Also update the teeIssueTeeState to keep it in sync
                setTeeIssueTeeData({ ovf: response.ovf, data: response.data || [] });
            } else {
                notify({ status: 'error', message: response.Message || 'Failed to fetch tees data' });
            }
        } catch (err) {
            notify({ status: 'error', message: 'Failed to fetch tees data' });
        } finally {
            setTeesLoading(false);
        }
    };

    // Socket connection for Tee updates
    useEffect(() => {
        if (isOpen) {
            // Fetch tees data when modal opens
            fetchTeesData();
            
            // Connect to Tee socket for real-time updates
            const teeChannel = echo.private('Tee-Primary');
            teeChannel.listen('.TeeEvents', (data) => {
                if (data.event === 'Updated' || data.event === 'Created') {
                    // Update specific tee in teesData
                    if (data.data && data.data.id) {
                        setTeesData(prevTees => {
                            const existingIndex = prevTees.findIndex(tee => tee.id === data.data.id);
                            if (existingIndex !== -1) {
                                // Update existing tee
                                const updatedTees = [...prevTees];
                                updatedTees[existingIndex] = { ...updatedTees[existingIndex], ...data.data };
                                return updatedTees;
                            } else {
                                // Add new tee if not found
                                return [...prevTees, data.data];
                            }
                        });
                    }
                } else if (data.event === 'Deleted') {
                    // Remove specific tee from teesData
                    if (data.data && typeof data.data === 'number') {
                        // Delete sockets come with data: id (number)
                        setTeesData(prevTees => prevTees.filter(tee => tee.id !== data.data));
                    }
                }
            });

            return () => {
                echo.leave('Tee-Primary');
            };
        }
        // eslint-disable-next-line
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && barcodeInputRef.current) {
            barcodeInputRef.current.focus();
        }
        // Clear previous data when modal opens
        if (isOpen) {
            setScannedTee(null);
            setSearchResults([]);
            setError('');
            setCart([]);
            setBuyerName('');
            setBuyerContact('');
        }
        // eslint-disable-next-line
    }, [isOpen]);

    // Multi-column search function for tee type and tee size
    const searchTeesByBarcode = (searchTerm) => {
        if (!searchTerm.trim()) return [];
        
        const searchTerms = searchTerm.trim().toLowerCase().split(/\s+/).filter(term => term.length > 0);
        
        if (searchTerms.length === 0) return [];
        
        return teesData.filter(tee => {
            // Get tee type and size names (handle both camelCase and snake_case properties)
            const teeTypeName = (tee.teeType?.name || tee.tee_type?.name || '').toLowerCase();
            const teeSizeName = (tee.teeSize?.name || tee.tee_size?.name || '').toLowerCase();
            const barcode = (tee.barcode || '').toLowerCase();
            
            // Check if ALL search terms match in ANY of the columns
            return searchTerms.every(term => 
                teeTypeName.includes(term) || 
                teeSizeName.includes(term) || 
                barcode.includes(term)
            );
        }).slice(0, 5); // Limit to 5 results
    };

    const handleBarcodeInputChange = (e) => {
        const value = e.target.value;
        setBarcodeInput(value);
        
        if (value.trim()) {
            const results = searchTeesByBarcode(value);
            setSearchResults(results);
            setError('');
        } else {
            setSearchResults([]);
            setScannedTee(null);
            setError(''); // Clear error when barcode input is cleared
        }
    };

    const handleBarcodeSubmit = (e) => {
        e.preventDefault();
        if (!barcodeInput.trim()) return;

        const results = searchTeesByBarcode(barcodeInput);
        
        if (results.length === 0) {
            setError('No tee found with this search term.');
            setScannedTee(null);
            return;
        }
        
        if (results.length === 1) {
            // Auto-select if only one result
            selectTee(results[0]);
        } else {
            // Show multiple results
            setSearchResults(results);
        }
    };

    const selectTee = (tee) => {
        // Check if tee has available stock
        const availableStock = tee.available || (tee.total_stock - (tee.issued || 0) - (tee.race_kit_reserved || 0) - (tee.race_kit_issued || 0));
        
        if (availableStock <= 0) {
            setError('This tee is out of stock.');
            setScannedTee(null);
            return;
        }
        
        setScannedTee(tee);
        setSearchResults([]);
        setError('');
        setQuantity(1);
    };

    const addToCart = () => {
        if (!scannedTee || quantity <= 0) return;

        const existingItem = cart.find(item => item.tee_id === scannedTee.id);
        
        if (existingItem) {
            // Update existing item quantity
            const newQuantity = existingItem.quantity + quantity;
            const availableStock = scannedTee.available || (scannedTee.total_stock - (scannedTee.issued || 0) - (scannedTee.race_kit_reserved || 0) - (scannedTee.race_kit_issued || 0));
            
            if (newQuantity > availableStock) {
                setError(`Cannot add more than available stock (${availableStock})`);
                return;
            }
            
            setCart(cart.map(item => 
                item.tee_id === scannedTee.id 
                    ? { ...item, quantity: newQuantity, total: newQuantity * item.price }
                    : item
            ));
        } else {
            // Add new item to cart
            const cartItem = {
                tee_id: scannedTee.id,
                tee: scannedTee,
                price: scannedTee.price,
                quantity: quantity,
                total: scannedTee.price * quantity
            };
            setCart([...cart, cartItem]);
        }

        // Clear scanned tee and reset form
        setScannedTee(null);
        setQuantity(1);
        setBarcodeInput('');
        setError('');
    };

    const removeFromCart = (index) => {
        setCart(cart.filter((_, i) => i !== index));
    };

    const updateCartQuantity = (index, newQuantity) => {
        if (newQuantity <= 0) {
            removeFromCart(index);
            return;
        }

        const item = cart[index];
        const availableStock = item.tee.available || (item.tee.total_stock - (item.tee.issued || 0) - (item.tee.race_kit_reserved || 0) - (item.tee.race_kit_issued || 0));
        
        if (newQuantity > availableStock) {
            setError(`Cannot set quantity higher than available stock (${availableStock})`);
            return;
        }

        setCart(cart.map((cartItem, i) => 
            i === index 
                ? { ...cartItem, quantity: newQuantity, total: newQuantity * cartItem.price }
                : cartItem
        ));
        setError('');
    };

    const getCartTotal = () => {
        return cart.reduce((sum, item) => sum + item.total, 0);
    };

    const processSale = async () => {
        if (cart.length === 0) return;

        setLoading(true);
        setError('');

        try {
            let hasErrors = false;
            const errors = [];

            // Process each item in cart
            for (const item of cart) {
                const saleData = {
                    Tee: item.tee_id,
                    Quantity: item.quantity,
                    BuyerName: buyerName || null,
                    BuyerContact: buyerContact || null,
                    Remarks: 'POS Sale'
                };

                try {
                    const response = await createDataRequest(saleData);
                    if (response.Status === 'error') {
                        hasErrors = true;
                        errors.push(`Item ${item.tee.barcode}: ${response.Message}`);
                    } else {
                        handleResponse(response);
                    }
                } catch (err) {
                    hasErrors = true;
                    errors.push(`Item ${item.tee.barcode}: ${err.message || 'Failed to process'}`);
                }
            }

            if (hasErrors) {
                // Show errors but don't close modal
                setError(errors.join('\n'));
                // Clear only successful items from cart
                // For now, we'll keep all items and let user retry
            } else {
                // All successful - clear cart and form fields but keep modal open for continuous sales
                setCart([]);
                setBuyerName('');
                setBuyerContact('');
                setBarcodeInput('');
                setScannedTee(null);
                setQuantity(1);
                setSearchResults([]);
                setError('');
                // Don't close modal - this is for continuous sales
                // toggle();
            }
        } catch (err) {
            setError(err.message || 'Failed to process sale. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Function to highlight search terms in text
    const highlightSearchTerms = (text, searchTerms) => {
        if (!text || !searchTerms || searchTerms.length === 0) return text;
        
        let highlightedText = text;
        searchTerms.forEach(term => {
            const regex = new RegExp(`(${term})`, 'gi');
            highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
        });
        
        return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
    };

    const clearCart = useCallback(() => {
        setCart([]);
        setScannedTee(null);
        setQuantity(1);
        setBarcodeInput('');
        setSearchResults([]);
        setError('');
    }, []);

    const removeLastAddedItem = useCallback(() => {
        if (cart.length > 0) {
            setCart(cart.slice(0, -1));
        }
    }, [cart]);

    // Enhanced keyboard shortcuts
    const handleKeyPress = (e) => {
        // Check for Ctrl/Cmd + key combinations
        if (e.ctrlKey || e.metaKey) {
            switch (e.key) {
                case 'Enter':
                    e.preventDefault();
                    if (cart.length > 0) {
                        processSale();
                    }
                    break;
                case 'Delete':
                    e.preventDefault();
                    removeLastAddedItem();
                    break;
                case 'Backspace':
                    e.preventDefault();
                    clearCart();
                    break;
                default:
                    break;
            }
            return;
        }

        // Regular Enter key handling
        if (e.key === 'Enter') {
            e.preventDefault();
            if (scannedTee) {
                addToCart();
            } else {
                handleBarcodeSubmit(e);
            }
        }
    };

    // Global keyboard shortcuts
    useEffect(() => {
        const handleGlobalKeyPress = (e) => {
            if (!isOpen) return;
            
            // Check for Ctrl/Cmd + key combinations
            if (e.ctrlKey || e.metaKey) {
                switch (e.key) {
                    case 'Enter':
                        e.preventDefault();
                        if (cart.length > 0) {
                            processSale();
                        }
                        break;
                    case 'Delete':
                        e.preventDefault();
                        removeLastAddedItem();
                        break;
                    case 'Backspace':
                        e.preventDefault();
                        clearCart();
                        break;
                    default:
                        break;
                }
            }
        };

        document.addEventListener('keydown', handleGlobalKeyPress);
        return () => document.removeEventListener('keydown', handleGlobalKeyPress);
        // eslint-disable-next-line
    }, [isOpen, cart.length, removeLastAddedItem, clearCart]);

    return (
        <>
            <style>
                {`
                    mark {
                        background-color: #fff3cd;
                        color: #856404;
                        padding: 2px 4px;
                        border-radius: 3px;
                        font-weight: bold;
                    }
                `}
            </style>
            <Modal isOpen={isOpen} toggle={toggle} size="xl" backdrop="static" keyboard={false}>
            <ModalHeader toggle={toggle}>
                <i className="mdi mdi-cart-outline me-2"></i>
                POS System&nbsp;&nbsp;
                <Button 
                    color="info" 
                    size="sm" 
                    className="ms-auto me-3"
                    title="Keyboard Shortcuts"
                    onClick={() => {
                        setShowShortcuts(true);
                    }}
                >
                    <i className="mdi mdi-information-outline"></i>
                </Button>
            </ModalHeader>
            <ModalBody>
                <Loader loading={loading || teesLoading} transparent />
                
                {/* Barcode Scanner Section */}
                <Card className="mb-3">
                    <CardBody>
                        <h6 className="text-primary mb-3">
                            <i className="mdi mdi-barcode-scan me-2"></i>
                            Search Tees
                        </h6>
                        <Form onSubmit={handleBarcodeSubmit}>
                            <Row>
                                <Col md={8}>
                                    <FormGroup>
                                        <Label for="barcode">Search or Scan Barcode</Label>
                                        <Input
                                            id="barcode"
                                            type="text"
                                            value={barcodeInput}
                                            onChange={handleBarcodeInputChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Search or scan barcode..."
                                            ref={barcodeInputRef}
                                            autoFocus
                                        />
                                        <small className="text-muted">
                                            <i className="mdi mdi-information-outline me-1"></i>
                                            Search across tee type, size, and barcode.
                                        </small>
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <Label>&nbsp;</Label>
                                        <Button 
                                            color="primary" 
                                            block 
                                            type="submit"
                                            disabled={!barcodeInput.trim()}
                                            title="Search"
                                        >
                                            <i className="mdi mdi-magnify"></i>
                                        </Button>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Form>

                        {/* Search Results */}
                        {searchResults.length > 0 && (
                            <div className="mt-3">
                                <h6 className="text-info mb-2">
                                    <i className="mdi mdi-magnify me-2"></i>
                                    Search Results ({searchResults.length}) for "{barcodeInput}"
                                    <small className="text-muted ms-2">
                                        ({barcodeInput.trim().split(/\s+/).filter(term => term.length > 0).length} search terms)
                                    </small>
                                </h6>
                                <div className="table-responsive">
                                    <Table size="sm" className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>Type</th>
                                                <th>Size</th>
                                                <th>Barcode</th>
                                                <th>Price</th>
                                                <th>Available</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {searchResults.map((tee, index) => {
                                                const availableStock = tee.available;
                                                const searchTerms = barcodeInput.trim().toLowerCase().split(/\s+/).filter(term => term.length > 0);
                                                return (
                                                    <tr key={index} className={availableStock <= 0 ? 'table-danger' : ''}>
                                                        <td>{highlightSearchTerms(tee.teeType?.name || tee.tee_type?.name, searchTerms)}</td>
                                                        <td>{highlightSearchTerms(tee.teeSize?.name || tee.tee_size?.name, searchTerms)}</td>
                                                        <td>
                                                            <code>{highlightSearchTerms(tee.barcode, searchTerms)}</code>
                                                        </td>
                                                        <td>{tee.price} MVR</td>
                                                        <td>
                                                            <Badge 
                                                                color={availableStock > 0 ? 'success' : 'danger'}
                                                            >
                                                                {availableStock}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <Button 
                                                                color="primary" 
                                                                size="sm"
                                                                onClick={() => selectTee(tee)}
                                                                disabled={availableStock <= 0}
                                                            >
                                                                <i className="mdi mdi-check me-1"></i>
                                                                Select
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        )}

                        {error && (
                            <Alert color="danger" className="mt-2">
                                <i className="mdi mdi-alert me-2"></i>
                                <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{error}</pre>
                            </Alert>
                        )}

                        {scannedTee && (
                            <div className="mt-3 p-3 bg-light rounded">
                                <h6 className="text-success mb-2">
                                    <i className="mdi mdi-check-circle me-2"></i>
                                    Tee Selected!
                                </h6>
                                <Row>
                                    <Col md={6}>
                                        <p className="mb-1"><strong>Type:</strong> {scannedTee.teeType?.name || scannedTee.tee_type?.name}</p>
                                        <p className="mb-1"><strong>Size:</strong> {scannedTee.teeSize?.name || scannedTee.tee_size?.name}</p>
                                        <p className="mb-1"><strong>Barcode:</strong> <code>{scannedTee.barcode}</code></p>
                                    </Col>
                                    <Col md={6}>
                                        <p className="mb-1"><strong>Price:</strong> {scannedTee.price} MVR</p>
                                        <p className="mb-1"><strong>Available:</strong> 
                                            <Badge color="success" className="ms-2">{scannedTee.available}</Badge>
                                        </p>
                                        <p className="mb-1"><strong>Total:</strong> {scannedTee.price * quantity} MVR</p>
                                    </Col>
                                </Row>
                                <Row className="mt-2">
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label for="quantity">Quantity</Label>
                                            <Input
                                                id="quantity"
                                                type="number"
                                                min="1"
                                                max={scannedTee.available}
                                                value={quantity}
                                                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                                                onKeyPress={handleKeyPress}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={8} className="d-flex align-items-end">
                                        <Button 
                                            color="success" 
                                            onClick={addToCart}
                                            disabled={quantity <= 0 || quantity > scannedTee.available}
                                            className="me-2"
                                        >
                                            <i className="mdi mdi-cart-plus me-1"></i>
                                            Add to Cart
                                        </Button>
                                        <Button 
                                            color="secondary" 
                                            onClick={() => {
                                                setScannedTee(null);
                                                setSearchResults([]);
                                            }}
                                        >
                                            <i className="mdi mdi-close me-1"></i>
                                            Cancel
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Cart Section */}
                <Card className="mb-3">
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="text-primary mb-0">
                                <i className="mdi mdi-cart me-2"></i>
                                Shopping Cart ({cart.length} items)
                            </h6>
                            {cart.length > 0 && (
                                <div>
                                    <Button 
                                        color="outline-warning" 
                                        size="sm" 
                                        onClick={removeLastAddedItem}
                                        className="me-2"
                                        title="Remove Last Item (Ctrl/Cmd + Delete)"
                                    >
                                        <i className="mdi mdi-delete-sweep me-1"></i>
                                        Remove Last
                                    </Button>
                                    <Button 
                                        color="outline-danger" 
                                        size="sm" 
                                        onClick={clearCart}
                                        title="Clear Cart (Ctrl/Cmd + Backspace)"
                                    >
                                        <i className="mdi mdi-cart-off me-1"></i>
                                        Clear Cart
                                    </Button>
                                </div>
                            )}
                        </div>

                        {cart.length === 0 ? (
                            <p className="text-muted text-center py-4">
                                <i className="mdi mdi-cart-outline me-2"></i>
                                No items in cart. Scan a barcode to add items.
                            </p>
                        ) : (
                            <div className="table-responsive">
                                <Table size="sm">
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th>Price</th>
                                            <th>Quantity</th>
                                            <th>Total</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cart.map((item, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <div>
                                                        <strong>{(item.tee.teeType?.name || item.tee.tee_type?.name) + ' - ' + (item.tee.teeSize?.name || item.tee.tee_size?.name)}</strong>
                                                        <br />
                                                        <small className="text-muted">Barcode: <code>{item.tee.barcode}</code></small>
                                                    </div>
                                                </td>
                                                <td>{item.price} MVR</td>
                                                <td>
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        max={item.tee.available}
                                                        value={item.quantity}
                                                        onChange={(e) => updateCartQuantity(index, parseInt(e.target.value) || 1)}
                                                        style={{ width: '80px' }}
                                                    />
                                                </td>
                                                <td>{item.total} MVR</td>
                                                <td>
                                                    <Button 
                                                        color="danger" 
                                                        size="sm"
                                                        onClick={() => removeFromCart(index)}
                                                    >
                                                        <i className="mdi mdi-delete"></i>
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="2"></td>
                                            <td><strong>Total:</strong></td>
                                            <td><strong>{getCartTotal()} MVR</strong></td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Customer Information */}
                <Card>
                    <CardBody>
                        <h6 className="text-primary mb-3">
                            <i className="mdi mdi-account me-2"></i>
                            Customer Information
                        </h6>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="buyerName">Customer Name</Label>
                                    <Input
                                        id="buyerName"
                                        type="text"
                                        value={buyerName}
                                        onChange={(e) => setBuyerName(e.target.value)}
                                        placeholder="Enter customer name (optional)"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="buyerContact">Contact Number</Label>
                                    <Input
                                        id="buyerContact"
                                        type="text"
                                        value={buyerContact}
                                        onChange={(e) => setBuyerContact(e.target.value)}
                                        placeholder="Enter contact number (optional)"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    <i className="mdi mdi-close me-1"></i>
                    Close
                </Button>
                <Button 
                    color="success" 
                    onClick={processSale}
                    disabled={cart.length === 0 || loading}
                    title="Process Sale (Ctrl/Cmd + Enter)"
                >
                    <i className="mdi mdi-cash-register me-1"></i>
                    Process Sale ({getCartTotal()} MVR)
                </Button>
            </ModalFooter>
        </Modal>

        {/* Keyboard Shortcuts Modal */}
        <Modal isOpen={showShortcuts} toggle={() => setShowShortcuts(false)} size="sm" centered>
            <ModalHeader toggle={() => setShowShortcuts(false)}>
                <i className="mdi mdi-keyboard me-2"></i>
                Keyboard Shortcuts
            </ModalHeader>
            <ModalBody>
                <h6 className="text-primary mb-3">POS System Keyboard Shortcuts</h6>
                <div className="table-responsive">
                    <Table size="sm" className="mb-0">
                        <thead>
                            <tr>
                                <th>Shortcut</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><kbd>Ctrl/Cmd + Enter</kbd></td>
                                <td>Process Sale</td>
                            </tr>
                            <tr>
                                <td><kbd>Ctrl/Cmd + Delete</kbd></td>
                                <td>Remove Last Item</td>
                            </tr>
                            <tr>
                                <td><kbd>Ctrl/Cmd + Backspace</kbd></td>
                                <td>Clear Cart</td>
                            </tr>
                            <tr>
                                <td><kbd>Enter</kbd></td>
                                <td>Add to Cart / Search</td>
                            </tr>
                        </tbody>
                    </Table>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={() => setShowShortcuts(false)}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
        </>
    );
};
